import { keepPreviousData, QueryClient, useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import type { Paged } from '@kts/shared';
import { ApiError, type Api } from './api';

export function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 20_000,
        gcTime: 5 * 60_000,
        retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
        refetchOnWindowFocus: true,
      },
      mutations: { retry: false },
    },
  });
}

/**
 * Sahifalangan ro'yxat. Filtr o'zgarganda eski natija xira holda qoladi (sakrashsiz),
 * yangisi kelganda almashadi.
 */
export function usePaged<T>(api: Api, path: string, params: Record<string, unknown>, opts: { enabled?: boolean; refetchInterval?: number } = {}) {
  return useQuery({
    queryKey: [path, params] as QueryKey,
    queryFn: ({ signal }) => api.get<Paged<T>>(path, params as never, signal),
    placeholderData: keepPreviousData,
    enabled: opts.enabled,
    refetchInterval: opts.refetchInterval,
  });
}

export function useApiQuery<T>(
  api: Api,
  path: string | null,
  params?: Record<string, unknown>,
  opts: { enabled?: boolean; refetchInterval?: number; staleTime?: number } = {},
) {
  return useQuery({
    queryKey: [path, params ?? {}] as QueryKey,
    queryFn: ({ signal }) => api.get<T>(path!, params as never, signal),
    enabled: path !== null && opts.enabled !== false,
    refetchInterval: opts.refetchInterval,
    staleTime: opts.staleTime,
  });
}

/** Mutatsiya + muvaffaqiyatda tegishli ro'yxatlarni yangilash (path prefiksi bo'yicha). */
export function useApiMutation<TVars, TRes = unknown>(fn: (vars: TVars) => Promise<TRes>, invalidate: string[] = []) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      await Promise.all(
        invalidate.map((p) =>
          qc.invalidateQueries({ predicate: (q) => typeof q.queryKey[0] === 'string' && (q.queryKey[0] as string).startsWith(p) }),
        ),
      );
    },
  });
}

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'VALIDATION' && Array.isArray(err.details) && err.details.length) {
      const first = err.details[0] as { path?: string; message?: string };
      return `${err.message}: ${first.path ? `${first.path.replace(/^\//, '')} — ` : ''}${first.message ?? ''}`;
    }
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return 'Noma’lum xato';
}
